import {
  getBrandKeyFromEnv,
  isVisibleForBrand,
  type BrandVisibilityRule,
} from "@/lib/branding";

/** Additional fields of the invite code registration form. */
export type RegistrationFieldKey =
  | "school"
  | "teacherName"
  | "class"
  | "registrantName"
  | "email"
  | "iin"
  | "password"
  | "regionBranch";

export type RegistrationFieldConfig = BrandVisibilityRule & {
  key: RegistrationFieldKey;
  labelKey: string;
  /** Mandatory fields are always enabled and cannot be toggled. */
  mandatory?: boolean;
  /**
   * Идентификатор участника. Логинов ровно один: включение одного гасит остальные.
   * Если для бренда виден только один такой, он ведёт себя как обязательный.
   */
  login?: boolean;
  /** Поле есть только у кодов этой школы: в редакторе сверяется со школой менеджера. */
  onlyForSchool?: (schoolId: number | null | undefined) => boolean;
};

/**
 * Школа АО «Казахтелеком» на trustexam: только её коды приглашения спрашивают регион, область,
 * населённый пункт и филиал. Бренд сверяем явно — id школы из базы другого бэкенда означает
 * другую школу.
 */
export const KAZAKHTELECOM_SCHOOL_ID = 1522;

export function isRegionBranchSchool(
  schoolId: number | null | undefined,
  brandKey: string = getBrandKeyFromEnv(),
): boolean {
  return brandKey === "trustexam" && schoolId === KAZAKHTELECOM_SCHOOL_ID;
}

export const REGISTRATION_FIELD_CONFIGS: readonly RegistrationFieldConfig[] = [
  { key: "school", labelKey: "ui-invite-form-fields-school" },
  { key: "teacherName", labelKey: "ui-invite-form-fields-teacher-name" },
  { key: "class", labelKey: "ui-invite-form-fields-class" },
  {
    key: "registrantName",
    labelKey: "ui-invite-form-fields-registrant-name",
    mandatory: true,
  },
  { key: "email", labelKey: "label-email", login: true },
  // ИИН is a Kazakhstan-only identifier and is not applicable to examru.
  { key: "iin", labelKey: "label-iin", login: true, hideForBrands: ["examru"] },
  { key: "password", labelKey: "label-password", mandatory: true },
  {
    key: "regionBranch",
    labelKey: "ui-invite-form-fields-region-branch",
    mandatory: true,
    onlyForSchool: isRegionBranchSchool,
  },
];

export function getVisibleRegistrationFields(
  brandKey: string = getBrandKeyFromEnv(),
  schoolId?: number | null,
): RegistrationFieldConfig[] {
  return REGISTRATION_FIELD_CONFIGS.filter(
    (field) =>
      isVisibleForBrand(field, brandKey) &&
      (!field.onlyForSchool || field.onlyForSchool(schoolId)),
  );
}

/**
 * Forces fields hidden for the current brand to be disabled.
 * Keeps rendering and validation in sync when a stored form still enables
 * a field that the brand no longer exposes.
 */
export function disableBrandHiddenFields<
  T extends Partial<Record<RegistrationFieldKey, boolean>>,
>(fields: T, brandKey: string = getBrandKeyFromEnv()): T {
  return REGISTRATION_FIELD_CONFIGS.reduce(
    (acc, field) =>
      isVisibleForBrand(field, brandKey) ? acc : { ...acc, [field.key]: false },
    fields,
  );
}

/** Ключи полей-идентификаторов, доступных бренду. */
export function getLoginFieldKeys(
  brandKey: string = getBrandKeyFromEnv(),
): RegistrationFieldKey[] {
  return getVisibleRegistrationFields(brandKey)
    .filter((field) => field.login)
    .map((field) => field.key);
}

/**
 * Приводит пару «ИИН / почта» к ровно одному включённому идентификатору: старые формы могли
 * сохранить оба или ни одного. Дефолт — первый доступный бренду (почта).
 */
export function normalizeLoginFields<
  T extends Partial<Record<RegistrationFieldKey, boolean>>,
>(fields: T, brandKey: string = getBrandKeyFromEnv()): T {
  const loginKeys = getLoginFieldKeys(brandKey);
  if (loginKeys.length === 0) return fields;

  const enabled = loginKeys.filter((key) => fields[key] === true);
  const chosen = enabled.length === 1 ? enabled[0] : loginKeys[0];

  return loginKeys.reduce(
    (acc, key) => ({ ...acc, [key]: key === chosen }),
    fields,
  );
}
